"use client"
import type React from "react"
import {
  type ComponentPropsWithRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useDebounceCallback, useIsOverflowed } from "../../hooks"
import { KeyboardDoubleArrowRight } from "../../icons"
import { classNames } from "../../utils"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandItemAvatar,
  CommandItemContent,
  CommandItemDescription,
  CommandItemTitle,
  CommandList,
  type CommandProps,
} from "../Command"
import { Flex } from "../Flex"
import { Popover, type PopoverProps } from "../Popover"
import { TextBody } from "../Text"

export type FloatingComboboxProps = Pick<
  PopoverProps,
  "open" | "onOpenChange" | "defaultOpen" | "content"
> &
  CommandProps & {
    overflowLabel?: React.ReactNode
    overrides?: {
      Popover?: Partial<PopoverProps>
    }
  }

function FloatingCombobox({
  children,
  open,
  onOpenChange,
  defaultOpen,
  content,
  shouldFilter,
  filter,
  label,
  overflowLabel = "Scroll for more",
  overrides,
  className,
  ...rest
}: FloatingComboboxProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [isOverflowed, setIsOverflowed] = useState(false)
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false)
  const isOverflowedDimensions = useIsOverflowed(ref)

  const checkOverflowed = useDebounceCallback(() => {
    if (ref.current) {
      const { scrollHeight, scrollWidth } = ref.current
      const { height, width } = ref.current.getBoundingClientRect()
      setIsOverflowed(scrollHeight > height + 1 || scrollWidth > width + 1)
    }
    // Just to check on next cycle
  }, 0)

  useEffect(() => {
    checkOverflowed()
  }, [checkOverflowed, isOverflowedDimensions])

  const checkScrollPosition = useCallback(() => {
    if (ref.current) {
      const { scrollTop, scrollHeight, clientHeight } = ref.current
      // Consider "at end" when within 40px of the bottom
      const atEnd = Math.abs(scrollHeight - scrollTop - clientHeight) <= 40

      setIsScrolledToEnd(atEnd)
    }
  }, [])

  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        checkOverflowed()
        setIsScrolledToEnd(false)
      }

      onOpenChange?.(open)
    },
    [checkOverflowed, onOpenChange],
  )

  useEffect(() => {
    const currentRef = ref.current
    if (currentRef) {
      currentRef.addEventListener("scroll", checkScrollPosition)
      // Initial check
      checkScrollPosition()

      return () => {
        currentRef.removeEventListener("scroll", checkScrollPosition)
      }
    }
  }, [checkScrollPosition, isOverflowed, open])

  return (
    <Popover
      content={
        <>
          <Command
            className={classNames("gap-0", className)}
            filter={filter}
            label={label}
            shouldFilter={shouldFilter}
            {...rest}
          >
            {content}
          </Command>
          {isOverflowed && (
            <Flex
              className={classNames(
                "sticky bottom-0 items-center justify-center gap-1",
                "h-7 rounded-b-xl bg-bg-secondary text-text-secondary outline outline-border-1",
                "slide-in-from-bottom animate-in opacity-100 duration-200 ease-out-quint",
                isScrolledToEnd && "h-0 overflow-hidden opacity-0",
              )}
            >
              <KeyboardDoubleArrowRight className="rotate-90" size={16} />
              <TextBody size="xs">{overflowLabel}</TextBody>
            </Flex>
          )}
        </>
      }
      defaultOpen={defaultOpen}
      onOpenChange={handleOnOpenChange}
      open={open}
      ref={ref}
      {...overrides?.Popover}
      className={classNames("rounded-xl p-0", overrides?.Popover?.className)}
    >
      {children}
    </Popover>
  )
}

export function FloatingComboboxInput({
  className,
  ...rest
}: ComponentPropsWithRef<typeof CommandInput>) {
  return (
    <CommandInput
      className={classNames(
        "sticky top-0 z-[1] rounded-b-none border-none outline outline-border-1",
        className,
      )}
      {...rest}
    />
  )
}

export function FloatingComboboxList({
  className,
  ...rest
}: ComponentPropsWithRef<typeof CommandList>) {
  return <CommandList className={classNames("p-1", className)} {...rest} />
}

export const FloatingComboboxEmpty = CommandEmpty

const FloatingComboboxItem = CommandItem

export const FloatingComboboxItemAvatar = CommandItemAvatar

export const FloatingComboboxItemTitle = CommandItemTitle

export const FloatingComboboxItemDescription = CommandItemDescription

export const FloatingComboboxItemContent = CommandItemContent

/**
 * A FloatingCombobox is an autocomplete input and command palette with a list of suggestions in a Popover.
 * The Popover is open when the user triggers a Select-style button.
 */
export { FloatingCombobox, FloatingComboboxItem }
